import { ProviderType } from "@wags/catalog/pure.ts";
import type {
    ContactListingState,
    ContactMessageIntent,
} from "@wags/profile-contracts/contact-session.contract.ts";
import type {
    ListingTransaction,
    MissingListingDetail,
} from "@/features/kits-conversations";
import {
    changedAdoptionStatus,
    listingOffersDelivery,
    type AdoptionStatus,
    type ChangedAdoptionStatus,
    type DogPhotoWellState,
    type ListingData,
} from "@/features/listings";
import { type MoneyRange, priceTransaction } from "@/lib/ui/price";
import { shareOfferDogName } from "./share-offer";

export type DogPageMode = "listing" | "draft";

export const REACH_OUT_LABEL = "Reach out";

export type HeaderAction = "cta" | "status-label" | "status-contact" | "draft";

export type ContactIntent = Extract<
    ContactMessageIntent,
    "available" | "similar"
>;

export type StatusContact = {
    label: string;
    compactLabel: string;
    intent: ContactIntent;
};

export type ListingActionArea =
    | {
          kind: "buy";
          price: string | null;
          contactable: boolean;
          label: string;
      }
    | {
          kind: "status";
          status: ChangedAdoptionStatus;
          contact: StatusContact | null;
      };

export type DogListingPresentation = {
    sticker: ChangedAdoptionStatus | null;
    action: ListingActionArea;
    headerAction: HeaderAction;
    showListingDetails: boolean;
    showAdoptedSummary: boolean;
};

export type DockPresentation = {
    docked: boolean;
    floating: "buy" | "status" | null;
    kits: boolean;
    iconSave: boolean;
};

type ShareOfferListing = Parameters<typeof shareOfferDogName>[0];

function labelWithName(
    dog: ShareOfferListing,
    named: string,
    fallback: string,
) {
    const name = shareOfferDogName(dog);
    return name && name.length <= 10 ? `${named} ${name}` : fallback;
}

function reachOutLabel(dog: ShareOfferListing) {
    return labelWithName(dog, "Reach out about", REACH_OUT_LABEL);
}

export function providerKindOf(providerType: ProviderType) {
    return providerType === ProviderType.Breeder ? "breeder" : "shelter";
}

const STATUS_CONTACT: Record<
    ChangedAdoptionStatus,
    (dog: ShareOfferListing) => StatusContact
> = {
    pending: (dog) => ({
        label: reachOutLabel(dog),
        compactLabel: REACH_OUT_LABEL,
        intent: "available",
    }),
    unavailable: (dog) => {
        const label = labelWithName(
            dog,
            "Find a dog like",
            "Find a similar dog",
        );
        return { label, compactLabel: label, intent: "similar" };
    },
};

const STATUS_HEADER_ACTION: Record<AdoptionStatus, HeaderAction> = {
    available: "cta",
    pending: "status-label",
    unavailable: "status-contact",
};

function actionArea(
    status: AdoptionStatus,
    listingMode: boolean,
    priceDisplay: string | null,
    dog: ShareOfferListing,
): ListingActionArea {
    const changed = changedAdoptionStatus(status);
    if (changed)
        return {
            kind: "status",
            status: changed,
            contact: listingMode ? STATUS_CONTACT[changed](dog) : null,
        };
    return {
        kind: "buy",
        price: priceDisplay,
        contactable: listingMode,
        label: reachOutLabel(dog),
    };
}

export function dogListingPresentation({
    status,
    mode,
    photoWellState,
    priceDisplay,
    dog,
}: {
    status: AdoptionStatus;
    mode: DogPageMode;
    photoWellState: DogPhotoWellState;
    priceDisplay: string | null;
    dog: ShareOfferListing;
}): DogListingPresentation {
    const listingMode = mode === "listing";
    return {
        sticker:
            photoWellState === "photos" ? changedAdoptionStatus(status) : null,
        action: actionArea(status, listingMode, priceDisplay, dog),
        headerAction: listingMode ? STATUS_HEADER_ACTION[status] : "draft",
        showListingDetails: status !== "unavailable",
        showAdoptedSummary: listingMode && status === "unavailable",
    };
}

function hasContact(action: ListingActionArea) {
    return action.kind === "buy" ? action.contactable : action.contact !== null;
}

export function dockPresentation(
    action: ListingActionArea,
    {
        copilotEnabled,
        actionRowOutOfView,
    }: { copilotEnabled: boolean; actionRowOutOfView: boolean },
): DockPresentation {
    const docked = actionRowOutOfView && hasContact(action);
    const savable =
        action.kind === "buy"
            ? action.contactable
            : action.status === "pending" && action.contact !== null;
    return {
        docked,
        floating:
            docked && !copilotEnabled
                ? action.kind === "buy"
                    ? "buy"
                    : "status"
                : null,
        kits: docked && copilotEnabled,
        iconSave: savable,
    };
}

export function listingTransaction(
    action: ListingActionArea,
    priceRange: MoneyRange | null | undefined,
): Exclude<ListingTransaction, { kind: "link" }> | null {
    if (action.kind === "status")
        return action.contact
            ? {
                  kind: "contact",
                  label: action.contact.compactLabel,
                  intent: action.contact.intent,
              }
            : null;
    if (!action.contactable) return null;
    return (
        priceTransaction(priceRange) ?? {
            kind: "contact",
            label: REACH_OUT_LABEL,
            intent: "available",
        }
    );
}

const STATUS_LISTING_STATE: Record<ChangedAdoptionStatus, ContactListingState> =
    { pending: "pending", unavailable: "adopted" };

export function contactListingState(
    status: AdoptionStatus,
    priceRange: MoneyRange | null | undefined,
): ContactListingState {
    const changed = changedAdoptionStatus(status);
    if (changed) return STATUS_LISTING_STATE[changed];
    return priceTransaction(priceRange) ? "priced" : "unpriced";
}

export function missingListingDetail(
    listingState: ContactListingState,
    loadedListing: ListingData | null,
): MissingListingDetail | undefined {
    if (!loadedListing) return undefined;
    if (listingState === "unpriced") return "fee";
    if (listingState !== "priced") return undefined;
    return listingOffersDelivery(loadedListing) ? undefined : "delivery";
}
